import React, { ReactElement, useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { Container } from '../components/Animations'
import ConfirmButtonModal from '../components/ConfirmButtonModal'
import Gate from '../components/Gate'
import Dropdown, { DropdownItem } from '../components/Dropdown'
import Dots from '../icons/Dots'
import Table from '../components/Table'
import Radom, { IPermissionRole } from '../state/Radom'
import { RADOM_COLORS } from '../util/Constants'
import { errorToast, successToast } from '../util/Util'
import { PermissionView } from './Team/APIRoles'
import {
  SettingsCard,
  SettingsHero,
  SettingsPage,
  SettingsSubtext,
  SettingsTitle,
  StatusPill
} from './Organization/SettingsLayout'
import { PrimaryButton } from '../components/Button'
import { usePermissions } from '../util/permissions'

const ADMIN_ROLE_ID = '00000000-0000-0000-0000-000000000000'
const READONLY_ROLE_ID = '00000000-0000-0000-0000-000000000001'
const EDITOR_ROLE_ID = '00000000-0000-0000-0000-000000000002'
const OPERATOR_ROLE_ID = '00000000-0000-0000-0000-000000000003'

const BUILT_IN_ROLE_IDS = new Set([ADMIN_ROLE_ID, READONLY_ROLE_ID, EDITOR_ROLE_ID, OPERATOR_ROLE_ID])

const RolePill = styled.span`
  background: ${RADOM_COLORS.GRAY_MED};
  border-radius: 6px;
  color: ${RADOM_COLORS.GRAY_DARKEST};
  font-size: 11px;
  font-weight: 500;
  padding: 3px 8px;
  white-space: nowrap;
`

const UserCountBadge = styled.span`
  background: ${RADOM_COLORS.GRAY_MED};
  border-radius: 20px;
  color: ${RADOM_COLORS.GRAY_DARKEST};
  font-size: 11px;
  font-weight: 500;
  padding: 2px 9px;
`

const RolesSettings = observer((): ReactElement => {
  const navigate = useNavigate()
  const [roles, setRoles] = useState([] as IPermissionRole[])
  const [isLoadingRoles, setIsLoadingRoles] = useState(true)
  const { has } = usePermissions()
  const listRoles = (): void => {
    setIsLoadingRoles(true)
    Radom.listPermissionRoles()
      .then(r => setRoles(r))
      .finally(() => setIsLoadingRoles(false))
  }

  useEffect(() => {
    listRoles()
  }, [])

  return (
    <Container>
      <SettingsPage>

        <SettingsHero>
          <div>
            <SettingsTitle>Roles</SettingsTitle>
            <SettingsSubtext>
              Define what each role can read or modify in your organization.
            </SettingsSubtext>
          </div>
          <Gate permission='WriteRoles'>
            <PrimaryButton onClick={() => navigate('/role/create')} style={{ gap: 8, whiteSpace: 'nowrap', minHeight: 36, fontSize: 13, padding: '0 14px' }}>
              <span style={{ fontSize: 14, lineHeight: 1 }}>+</span>
              <span>Create role</span>
            </PrimaryButton>
          </Gate>
        </SettingsHero>

        <SettingsCard>

          <Table
            mergeHeader
            disableHoverStyle
            disableOnClickState
            headers={['Role name', 'Members', 'Permissions', ''].map(h => <span key={h}>{h}</span>)}
            items={
              [...roles].sort((a, b) => {
                const aBuiltIn = BUILT_IN_ROLE_IDS.has(a.id) ? 0 : 1
                const bBuiltIn = BUILT_IN_ROLE_IDS.has(b.id) ? 0 : 1
                return aBuiltIn - bBuiltIn
              }).map(r => {
                const memberCount = r.users.length
                const isBuiltIn = BUILT_IN_ROLE_IDS.has(r.id)

                return [
                  <div key="roleName" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ color: 'var(--ink, #20242d)', fontSize: 13, fontWeight: 500 }}>{r.name}</span>
                    {isBuiltIn && (
                      <StatusPill $tone='neutral' style={{ fontSize: 10 }}>Built-in</StatusPill>
                    )}
                  </div>,
                  <div key="users" style={{ display: 'flex', alignItems: 'center' }}>
                    {memberCount > 0
                      ? <UserCountBadge>{memberCount} {memberCount === 1 ? 'member' : 'members'}</UserCountBadge>
                      : <span style={{ color: RADOM_COLORS.GRAY_DARKER, fontSize: 13 }}>—</span>}
                  </div>,
                  <div key="permissions" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    {r.id === ADMIN_ROLE_ID && (
                      <RolePill style={{
                        background: 'var(--pill-blue-bg, #e7ecff)',
                        color: 'var(--pill-blue-ink, #2a50c8)'
                      }}>All permissions</RolePill>
                    )}
                    {r.id === READONLY_ROLE_ID && (
                      <RolePill>Read only</RolePill>
                    )}
                    {r.id !== ADMIN_ROLE_ID && r.id !== READONLY_ROLE_ID &&
                      <PermissionView permissions={r.permissions} />}
                  </div>,
                  <div key="actions" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    {!isBuiltIn && has('WriteRoles') && (
                      <Dropdown
                        key={r.id}
                        noChevron
                        iconTrigger
                        selectedContent={<Dots width={15} />}
                        onCloseFn={() => {}}
                        dropdownContent={
                          <div style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                            <DropdownItem onClick={() => navigate(`/role/${r.id}/edit`)}>
                              Edit role
                            </DropdownItem>
                            <ConfirmButtonModal
                              title='Delete role'
                              description='Are you sure you want to delete this role? All team members with this role will lose access.'
                              action='Delete role'
                              onConfirm={async () => {
                                Radom.deletePermissionRole(r.id)
                                  .then(() => {
                                    successToast('Role deleted')
                                    listRoles()
                                  })
                                  .catch(err => {
                                    errorToast(err.response?.data?.error || err.message)
                                  })
                              }}
                              ActionComponent={(props: React.HTMLAttributes<HTMLDivElement>) =>
                                <DropdownItem {...props} style={{ color: RADOM_COLORS.ERROR }}>
                                  Delete role
                                </DropdownItem>
                              }
                            />
                          </div>
                        }
                      />
                    )}
                  </div>
                ]
              })
            }
            isLoading={isLoadingRoles}
            emptyPlaceholderText='No custom roles yet'
            onClickRow={() => {}}
          />
        </SettingsCard>

      </SettingsPage>
    </Container>
  )
})

export default RolesSettings
